import axios from 'axios';
import {
  getRefreshToken,
  setRefreshToken,
  getAccessToken,
  setAccessToken,
  checkAndSaveRefreshTokenFromURL,
  checkAndSaveRedirectUrlFromURL,
  checkAndSaveCompanyFromURL,
  checkAndSaveUserIdFromURL,
  setUserInfo
} from '../utils/tokenManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Refresh access token using refresh token
 * Backend response: { status: 'Success', message: { accessToken: "Bearer ..." } }
 */
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    console.log('Refreshing access token...');
    const response = await axios.get(`${BACKEND_URL}/api/auth/token`, {
      headers: {
        Authorization: refreshToken
      },
      timeout: 8000
    });

    const newAccessToken = response.data.message?.accessToken;
    const newRefreshToken = response.data.message?.refreshToken;

    if (newAccessToken) {
      setAccessToken(newAccessToken);
      if (newRefreshToken) {
        setRefreshToken(newRefreshToken);
      }
      console.log('Access token refreshed successfully');
      return newAccessToken;
    } else {
      throw new Error('No access token in response');
    }
  } catch (error) {
    console.error('Failed to refresh access token:', error);
    throw error;
  }
};

/**
 * Sync user info from VIO via the backend
 * Backend response: { status: 'Success', message: { userId, username, email, ... } }
 */
const syncUserInfo = async () => {
  try {
    console.log('Syncing user info from VIO...');
    const response = await api.get('/users/sync');

    if (response.data.status === 'Success' && response.data.message) {
      const userInfo = response.data.message;
      setUserInfo(userInfo);
      console.log('User info synced successfully');
      return userInfo;
    } else {
      throw new Error('Invalid response from user sync');
    }
  } catch (error) {
    console.error('Failed to sync user info:', error);
    throw error;
  }
};

/**
 * Initialize auth on app start.
 *
 * 1. Capture URL params (refreshToken, redirectUrl, company, userId).
 * 2. Strip them from window.location for safety.
 * 3. If we have a refresh token, exchange it for an access token and sync the user.
 */
export const initializeAuth = async () => {
  try {
    const urlRefreshToken = checkAndSaveRefreshTokenFromURL();
    checkAndSaveRedirectUrlFromURL();
    checkAndSaveCompanyFromURL();
    checkAndSaveUserIdFromURL();

    const sensitiveParams = ['refreshToken', 'redirectUrl', 'company', 'userId'];
    const url = new URL(window.location.href);
    let cleaned = false;
    sensitiveParams.forEach((p) => {
      if (url.searchParams.has(p)) {
        url.searchParams.delete(p);
        cleaned = true;
      }
    });
    if (cleaned) window.history.replaceState({}, document.title, url.pathname + url.search);

    const refreshToken = urlRefreshToken || getRefreshToken();

    if (refreshToken) {
      await refreshAccessToken();
      await syncUserInfo();
      return { success: true, hasRefreshToken: true };
    } else {
      console.log('No refresh token found');
      return { success: false, hasRefreshToken: false };
    }
  } catch (error) {
    console.error('Failed to initialize auth:', error);
    return { success: false, hasRefreshToken: !!getRefreshToken() };
  }
};

api.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = accessToken;
    }
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      config.headers['x-refresh-token'] = refreshToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = token;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers.Authorization = newAccessToken;
        processQueue(null, newAccessToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        console.error('Token refresh failed, user needs to re-authenticate');
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response) {
      console.error('API Error:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('Network Error:', error.request);
    } else {
      console.error('Error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default api;
